import React from "react";
import {
    Button,
    Box,
    FormControl,
    FormLabel,
    Image,
    Text,
    Grid,
    useMediaQuery,
    Flex,
    Spinner,
    Icon,
    Textarea,
    Link,
    Badge
} from "@chakra-ui/react";
import { FaRobot } from "react-icons/fa";
import { AddIcon } from "../../components/Icons/AddIcon";
import { FormInput } from "../../components/FormInput";
import { useStoreSetting } from './useStoreSetting';
import { ModalBox } from "../../components/ModalBox";
import { MediaPicker } from "../../components/MediaPicker";
import { LoadingOverlay } from "../../components/LoadingOverlay"
import { WYSIWYGEditor } from "../../components/WYSIWYGEditor"
import { cleanHtml } from "../../utils/helper"
import { ResourcesPage } from "../ResourcesPage";
import { HelmetComponent } from "../../components/HelmetComponent";
import { FormTextArea } from "../../components/FormTextArea";
import { FormInputWithAddons } from "../../components/FormInputWithAddons";

export const StoreSettingsPage = () => {

    const {
        storeSetting,
        updateStoreSetting,
        isOpen,
        onOpen,
        onClose,
        onChange,
        imageUrl,
        videoUrl,
        selectMedia,
        loading,
        videoOnOpen,
        videoIsOpen,
        videoOnClose,
        onCancelImageModal,
        onCancelVideoModal,
        user,
        building,
        prompt,
        isImageLoading,
        setIsImageLoading,
        setPrompt,
        createStoreAbout,
        pptOnOpen,
        pptIsOpen,
        pptUrl,
        onCancelPPTModal,
        resetPptTheme,
        onConfirmPptTheme,
        pptTheme,
    } = useStoreSetting()

    const [largeScreen] = useMediaQuery("(max-width: 1100px)");
    const [smallScreen] = useMediaQuery("(max-width: 850px)");

    return (
        <>
            <HelmetComponent title={`Store Setting - Home - ${user.firstName} ${user.lastName}`} />
            {loading.isLoading && <LoadingOverlay loadingMessage={loading.loadingMessage} />}
            <Box bg="gray.50" p={4} rounded="lg">
                <Flex justify="space-between" align="center">
                    <Flex align="center">
                        <Icon as={FaRobot} color="blue.500" />
                        <Text ml={2} fontWeight="medium">Generate Store Logo, About Us, SEO Title, and Description with AI</Text>
                    </Flex>
                </Flex>
                <Textarea
                    mt={4}
                    placeholder="Create store content for a platform called EduNova that sells online video courses and digital learning bundles for students and teachers."
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                />
                <Button w="full" mt={4} colorScheme="blue" onClick={createStoreAbout} isDisabled={!prompt}>
                    Generate Store Details
                </Button>
            </Box>
            <form onSubmit={updateStoreSetting}>
                {building && (
                    <Box bg="orange.50" border="1px solid" borderColor="orange.200" rounded="md" p={3} mt={3} mb={3}>
                        <Text fontSize="sm" color="orange.700">Build in progress. Fields that trigger a rebuild are temporarily disabled.</Text>
                    </Box>
                )}
                <Grid templateColumns={`${smallScreen ? "repeat(1, 1fr)" : "repeat(2, 1fr)"}`} gap={`${smallScreen ? 0 : 3}`}>
                    <FormInput
                        label="Business Name"
                        name="publisherName"
                        type="text"
                        inputOnChange={onChange}
                        value={storeSetting?.publisherName}
                        formProps={{ isRequired: true }}
                        padding="20px 10px 0 0"
                    />
                    <FormInput
                        label="Email address"
                        name="publisherEmail"
                        type="email"
                        value={storeSetting?.owner?.email}
                        padding="20px 0 0"
                        isDisabled={true}
                    />
                    <FormInput
                        label="Domain Name"
                        name="domainName"
                        type="text"
                        value={storeSetting?.domainName}
                        padding="20px 0 0"
                        isDisabled={true}
                    />
                </Grid>
                <Grid templateColumns={`${smallScreen ? "repeat(1, 1fr)" : "repeat(2, 1fr)"}`} gap={`${smallScreen ? 0 : 3}`}>
                    <FormControl padding="20px 10px 0 0" isRequired>
                        <FormLabel
                            fontSize="16px"
                            textTransform="capitalize"
                            fontWeight={600}
                        >
                            Logo
                        </FormLabel>
                        <Text fontSize="xs" color="orange.500" mb={2}>
                            Changing logo triggers a website rebuild
                        </Text>
                        {
                            imageUrl ?
                                <>
                                    <Box height="auto" overflow="hidden" >
                                        <Image
                                            src={imageUrl}
                                            width="180px"
                                            objectFit='contain'
                                            opacity={isImageLoading ? 0.5 : 1}
                                            transition="opacity 0.3s ease"
                                        />
                                    </Box>
                                    <Button onClick={onOpen} isDisabled={building} variant="ghost" colorScheme="blue" fontSize="sm" textDecorationLine="underline">Edit</Button>
                                </> :
                                <Box
                                    w="200px"
                                    h="40px"
                                    background="#edf2f7"
                                    borderRadius="4px"
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    position="relative"
                                    onClick={building ? undefined : onOpen}
                                    pointerEvents={building ? 'none' : 'auto'}
                                    opacity={building ? 0.6 : 1}
                                >
                                    <AddIcon />
                                </Box>
                        }
                    </FormControl>
                    <FormControl padding="20px 0 0" isRequired>
                        <FormLabel
                            fontSize="16px"
                            textTransform="capitalize"
                            fontWeight={600}
                        >
                            Video Introduction
                        </FormLabel>
                        {
                            videoUrl ?
                                <>
                                    <Box
                                        as="iframe"
                                        padding={0}
                                        src={videoUrl}
                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                        allowFullScreen
                                    />
                                    <Button onClick={videoOnOpen} variant="ghost" colorScheme="blue" fontSize="sm" textDecorationLine="underline">Edit</Button>
                                </> :
                                <Box
                                    w="200px"
                                    h="40px"
                                    background="#edf2f7"
                                    borderRadius="4px"
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    position="relative"
                                    onClick={videoOnOpen}
                                >
                                    <AddIcon />
                                </Box>
                        }
                    </FormControl>
                </Grid>
                <Box padding="20px 0 0">
                    <FormLabel
                        fontSize="16px"
                        textTransform="capitalize"
                        fontWeight={600}
                    >
                        PPT Theme
                    </FormLabel>
                    {pptTheme ? <Link href={pptTheme}>{pptTheme.split("/").pop()}</Link> : <></>}
                    <Button onClick={pptOnOpen} variant="ghost" colorScheme="blue" fontSize="sm" textDecorationLine="underline">Select From Resources</Button>
                    <Button onClick={resetPptTheme} variant="ghost" colorScheme="blue" fontSize="sm" textDecorationLine="underline">Set the default</Button>
                </Box>
                <FormControl padding="20px 0 0" isRequired>
                    <FormLabel
                        fontSize="16px"
                        textTransform="capitalize"
                        fontWeight={600}
                    >
                        About
                    </FormLabel>
                    <WYSIWYGEditor
                        initialContent={cleanHtml(storeSetting?.about)}
                        handleChange={(e) => onChange(e, "about")}
                    />
                </FormControl>
                <Box padding="20px 0">
                    <Flex align="center" justify="space-between" flexWrap="wrap">
                        <Text fontSize='xl' fontWeight="600">SEO Settings</Text>
                        <Flex align="center" gap={2}>
                            <Badge colorScheme="orange">Triggers rebuild</Badge>
                            <Text fontSize="xs" color="gray.600">SEO title, description and social links</Text>
                        </Flex>
                    </Flex>
                    <FormInput
                        label="SEO Title"
                        name="seoTitle"
                        type="text"
                        inputOnChange={onChange}
                        value={storeSetting?.seoTitle}
                        padding="20px 10px 0 0"
                        isDisabled={building}
                    />
                    <FormTextArea
                        label="SEO Description"
                        name="seoDescription"
                        type="text"
                        inputOnChange={onChange}
                        value={storeSetting?.seoDescription}
                        padding="20px 0 0"
                        isReadOnly={building}
                    />
                    <Grid templateColumns={`${smallScreen ? "repeat(1, 1fr)" : "repeat(2, 1fr)"}`} gap={3}>
                        <FormInputWithAddons
                            label="Instagram"
                            name="instagram"
                            placeholder="username"
                            type="text"
                            inputOnChange={onChange}
                            value={storeSetting?.instagram}
                            padding="20px 10px 0 0"
                            leftAddon="https://www.instagram.com/"
                            isDisabled={building}
                        />
                        <FormInputWithAddons
                            label="Facebook"
                            name="facebook"
                            placeholder="username"
                            type="text"
                            inputOnChange={onChange}
                            value={storeSetting?.facebook}
                            padding="20px 10px 0 0"
                            leftAddon="https://www.facebook.com/"
                            isDisabled={building}
                        />
                        <FormInputWithAddons
                            label="Linkedin"
                            name="linkedin"
                            placeholder="companyname"
                            type="text"
                            inputOnChange={onChange}
                            value={storeSetting?.linkedin}
                            padding="20px 10px 0 0"
                            leftAddon="https://www.linkedin.com/company/"
                            isDisabled={building}
                        />
                        <FormInputWithAddons
                            label="X"
                            name="x"
                            placeholder="username"
                            type="text"
                            inputOnChange={onChange}
                            value={storeSetting?.x}
                            padding="20px 0 0"
                            leftAddon="https://x.com/"
                            isDisabled={building}
                        />
                    </Grid>
                </Box>
                <Flex alignItems="center" padding="20px 0 0">
                    <Button
                        colorScheme="blue"
                        background="#1b6ec2"
                        fontWeight="600"
                        type="submit"
                        margin="0 10px 0 0"
                        disabled={building || !imageUrl || !videoUrl || storeSetting?.about === ""}
                    >
                        Update
                    </Button>
                    {building ? <><Spinner size='xs' color='orange.400' margin="0 5px 0 0" /><Text fontWeight="600" color="orange.400">Building EdShop</Text></> : <></>}
                </Flex>
            </form>
            <ModalBox
                title="Select Logo"
                isOpen={isOpen}
                onClose={onCancelImageModal}
                isFooter={true}
                onCancel={onCancelImageModal}
                confirmButtonType={true}
                onConfirm={onClose}
                confirmButtonText="Select"
                size="full"
                isHideDivider={true}
                isConfirmButtonDisabled={!imageUrl}
                description={
                    <ResourcesPage type={"images"} isHideFilter={true} isSelection={true} imageUrl={imageUrl} selectMedia={selectMedia} isLogo={true} />
                }
            ></ModalBox>
            <ModalBox
                title="Video"
                isOpen={videoIsOpen}
                onClose={onCancelVideoModal}
                isFooter={true}
                onCancel={onCancelVideoModal}
                confirmButtonType={true}
                onConfirm={videoOnClose}
                confirmButtonText="Select"
                size="full"
                isHideDivider={true}
                isConfirmButtonDisabled={!videoUrl}
                description={
                    <ResourcesPage type={"videos"} isHideFilter={true} isSelection={true} videoUrl={videoUrl} selectMedia={selectMedia} />
                }
            ></ModalBox>
            <ModalBox
                title="PPT"
                isOpen={pptIsOpen}
                onClose={onCancelPPTModal}
                isFooter={true}
                onCancel={onCancelPPTModal}
                confirmButtonType={true}
                onConfirm={onConfirmPptTheme}
                confirmButtonText="Select"
                size="full"
                isHideDivider={true}
                isConfirmButtonDisabled={!pptUrl}
                description={
                    <ResourcesPage type={"ppts"} isHideFilter={true} isSelection={true} pptUrl={pptUrl} selectMedia={selectMedia} />
                }
            ></ModalBox>
        </>
    );
};
